import { Typography } from '@pesto/ui';
import { type Column, SimpleTable } from '@pesto/ui/components/SimpleTable';

import type { PdfTransaction, TransactionCategoryKey } from '../types/Statements.types';

import { pdfDataSnapshotSchema } from '../utils/pdfDataSnapshotSchema';

interface AccountActivitySectionProps {
  pdfDataSnapshot: unknown;
}

const transactionColumns: Column<PdfTransaction>[] = [
  {
    id: 'posted_date',
    header: 'Posted',
    accessor: (row) => row.posted_date,
    width: '110px',
    cellClassName: 'whitespace-nowrap tabular-nums',
  },
  {
    id: 'transaction_date',
    header: 'Trans Date',
    accessor: (row) => row.transaction_date,
    width: '110px',
    cellClassName: 'whitespace-nowrap tabular-nums',
  },
  {
    id: 'description',
    header: 'Description',
    accessor: (row) => row.description,
  },
  {
    id: 'amount',
    header: 'Amount',
    accessor: (row) => row.amount,
    width: '90px',
    cellClassName: 'text-right tabular-nums',
    headerClassName: 'text-right',
  },
];

const TRANSACTION_CATEGORIES: [TransactionCategoryKey, string][] = [
  ['purchases', 'Purchases'],
  ['payments', 'Payments'],
  ['fees', 'Fees'],
  ['interest', 'Interest'],
  ['drawdowns', 'Drawdowns'],
];

export const AccountActivitySection = ({ pdfDataSnapshot }: AccountActivitySectionProps) => {
  const parsedSnapshot = pdfDataSnapshotSchema.safeParse(pdfDataSnapshot);
  const accountActivity = parsedSnapshot.success ? parsedSnapshot.data.account_activity : undefined;

  if (!accountActivity) return null;

  const categories = TRANSACTION_CATEGORIES.flatMap(([key, label]) => {
    const category = accountActivity[key];

    return category?.transactions.length ? [{ key, label, category }] : [];
  });

  if (!categories.length) return null;

  const totalItems = categories.reduce((sum, { category }) => sum + (category.count ?? 0), 0);
  const totalActivityDisplay = accountActivity.total_transactions ?? null;

  return (
    <div className="flex h-full flex-col">
      <Typography variant="body" className="text-muted-foreground mb-3 text-xs font-medium uppercase tracking-wide">
        Account Activity
      </Typography>
      <div className="space-y-4">
        {categories.map(({ key, label, category }) => {
          return (
            <div key={key}>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-sm font-medium">{label}</span>
                <span className="text-muted-foreground text-xs tabular-nums">
                  {category.count} items — {category.subtotal}
                </span>
              </div>
              <SimpleTable data={category.transactions} columns={transactionColumns} size="sm" enableHorizontalScroll />
            </div>
          );
        })}
        {totalActivityDisplay ? (
          <div className="border-muted-foreground/20 mt-3 border-t pt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Total activity</span>
              <span className="text-muted-foreground text-xs tabular-nums">
                {totalItems} items — {totalActivityDisplay}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
